/**
 * Stock Item Duplicate Dialog
 * Allows users to duplicate one or more stock items with code editing
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Copy,
  CheckCircle2,
  AlertCircle,
  Info,
  Package,
  DollarSign,
  MapPin,
  User,
} from 'lucide-react';
import {
  DuplicateItem,
  DuplicateValidationResult,
  validateNewCode,
  getDuplicationSummary,
} from '@/lib/stock-item-duplicate-logic';

interface StockItemDuplicateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  validationResult: DuplicateValidationResult;
  allStockCodes: string[];
  onConfirm: (items: DuplicateItem[]) => void;
}

export function StockItemDuplicateDialog({
  open,
  onOpenChange,
  validationResult,
  allStockCodes,
  onConfirm,
}: StockItemDuplicateDialogProps) {
  const [items, setItems] = useState<DuplicateItem[]>([]);
  const [codeErrors, setCodeErrors] = useState<Record<string, string>>({});

  // Initialize items when dialog opens
  useEffect(() => {
    if (open) {
      setItems(validationResult.items);
      setCodeErrors({});
    }
  }, [open, validationResult]);

  // Validate all codes
  const validateAllCodes = () => {
    const errors: Record<string, string> = {};
    const newCodes = items.map((item) => item.newCode);

    items.forEach((item, index) => {
      const otherCodes = newCodes.filter((_, i) => i !== index);
      const validation = validateNewCode(item.newCode, allStockCodes, otherCodes);
      
      if (!validation.isValid && validation.error) {
        errors[item.original.code] = validation.error;
      }
    });

    setCodeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle code change
  const handleCodeChange = (originalCode: string, newCode: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.original.code === originalCode ? { ...item, newCode } : item
      )
    );
    
    // Clear error for this item
    setCodeErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[originalCode];
      return newErrors;
    });
  };

  // Handle active toggle
  const handleActiveToggle = (originalCode: string, isActive: boolean) => {
    setItems((prev) =>
      prev.map((item) =>
        item.original.code === originalCode ? { ...item, isActive } : item
      )
    );
  };

  // Handle confirm
  const handleConfirm = () => {
    if (validateAllCodes()) {
      onConfirm(items);
    }
  };

  const summary = getDuplicationSummary();
  const isSingleItem = items.length === 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Duplicate Stock Item{!isSingleItem && 's'}
          </DialogTitle>
          <DialogDescription>
            {isSingleItem
              ? 'Create a duplicate of the selected stock item with a new code.'
              : `Create duplicates of ${items.length} stock items with new codes.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full pr-4">
            <div className="space-y-6">
              {/* Items to Duplicate */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Items to Duplicate
                </h3>
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={item.original.code}
                      className="border rounded-lg p-4 space-y-3"
                    >
                      {/* Original Item Info */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-semibold">
                              {item.original.code}
                            </span>
                            {item.original.inactive && (
                              <Badge variant="secondary" className="text-xs">
                                Inactive
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {item.original.description}
                          </p>
                          
                          {/* Quick Info */}
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                            {item.original.category && (
                              <span className="flex items-center gap-1">
                                <Package className="h-3 w-3" />
                                {item.original.category}
                              </span>
                            )}
                            {item.original.unitPrice > 0 && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="h-3 w-3" />
                                £{item.original.unitPrice.toFixed(2)}
                              </span>
                            )}
                            {(item.original.warehouseCode || item.original.locationCode) && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {item.original.warehouseCode} - {item.original.locationCode}
                              </span>
                            )}
                            {item.original.supplier && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {item.original.supplier}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* New Code Input */}
                      <div className="space-y-2">
                        <Label htmlFor={`code-${item.original.code}`}>
                          New Stock Code *
                        </Label>
                        <Input
                          id={`code-${item.original.code}`}
                          value={item.newCode}
                          onChange={(e) =>
                            handleCodeChange(item.original.code, e.target.value.toUpperCase())
                          }
                          placeholder="Enter new stock code"
                          className={codeErrors[item.original.code] ? 'border-red-500' : ''}
                        />
                        {codeErrors[item.original.code] && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {codeErrors[item.original.code]}
                          </p>
                        )}
                      </div>

                      {/* Active Toggle */}
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`active-${item.original.code}`} className="text-sm">
                          Create as active item
                        </Label>
                        <Switch
                          id={`active-${item.original.code}`}
                          checked={item.isActive}
                          onCheckedChange={(checked) =>
                            handleActiveToggle(item.original.code, checked)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Warnings */}
              {validationResult.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 text-amber-900">
                    <AlertCircle className="h-4 w-4" />
                    Important Notes
                  </h3>
                  <ul className="space-y-1">
                    {validationResult.warnings.map((warning, index) => (
                      <li key={index} className="text-xs text-amber-800 flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{warning}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What Will Be Copied */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-blue-900">
                  <Info className="h-4 w-4" />
                  What Will Be Duplicated
                </h3>
                
                <div className="space-y-3">
                  {/* Copied Fields */}
                  <div>
                    <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Fields that will be copied:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {summary.copied.map((field) => (
                        <Badge key={field} variant="secondary" className="text-xs">
                          {field}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Reset Fields */}
                  <div>
                    <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Fields that will be reset:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {summary.reset.map((field) => (
                        <Badge key={field} variant="outline" className="text-xs">
                          {field}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="pt-2 border-t border-blue-200">
                    <ul className="space-y-1">
                      {summary.notes.map((note, index) => (
                        <li key={index} className="text-xs text-blue-800 flex items-start gap-2">
                          <span className="mt-0.5">•</span>
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!validationResult.canProceed}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate {items.length > 1 ? `${items.length} Items` : 'Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
